'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    SiteBanner,
    SiteBannerPlacement,
    deleteSiteBanner,
    getSiteBanners,
    getSitePlacements,
    reorderSiteBanners,
    toggleSiteBanner,
} from '@/lib/api/marketing/site-banners-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdDelete,
    MdEdit,
    MdOpenInNew,
    MdViewCarousel,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import SiteVideoCard from './site-video-card'

const ALL_PLACEMENTS = 'todos'

/** Situação real do banner: ativo não basta, a janela de exibição manda. */
function bannerStatus(banner: SiteBanner): {
    label: string
    className: string
} {
    if (!banner.active) return { label: 'Inativo', className: 'badge-ghost' }

    const now = Date.now()
    if (banner.startsAt && new Date(banner.startsAt).getTime() > now) {
        return { label: 'Agendado', className: 'badge-info' }
    }
    if (banner.endsAt && new Date(banner.endsAt).getTime() < now) {
        return { label: 'Expirado', className: 'badge-warning' }
    }
    return { label: 'No ar', className: 'badge-success' }
}

function formatDate(value: string): string {
    return new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

/** Só uma das pontas da janela costuma vir preenchida; "de X até -" não se lê. */
function formatWindow(banner: SiteBanner): string {
    if (banner.startsAt && banner.endsAt) {
        return `${formatDate(banner.startsAt)} até ${formatDate(banner.endsAt)}`
    }
    if (banner.startsAt) return `A partir de ${formatDate(banner.startsAt)}`
    if (banner.endsAt) return `Até ${formatDate(banner.endsAt)}`
    return ''
}

export default function SiteBannersListPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'marketing',
        'site_banners',
    )

    const [loading, setLoading] = useState(true)
    const [placements, setPlacements] = useState<SiteBannerPlacement[]>([])
    const [banners, setBanners] = useState<SiteBanner[]>([])
    const [selectedPlacement, setSelectedPlacement] = useState(ALL_PLACEMENTS)
    const [reordering, setReordering] = useState(false)

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadAll()
    }, [])

    const loadAll = async () => {
        setLoading(true)
        try {
            const [placementsRes, bannersRes] = await Promise.all([
                getSitePlacements(),
                getSiteBanners(),
            ])
            if (placementsRes.success && Array.isArray(placementsRes.result)) {
                setPlacements(placementsRes.result)
            }
            if (bannersRes.success && Array.isArray(bannersRes.result)) {
                setBanners(bannersRes.result)
            }
        } catch {
            toast.error('Erro ao carregar os banners do site')
        } finally {
            setLoading(false)
        }
    }

    const placementLabel = (key: string) =>
        placements.find((placement) => placement.key === key)?.label ?? key

    const visibleBanners = useMemo(() => {
        const list =
            selectedPlacement === ALL_PLACEMENTS
                ? banners
                : banners.filter(
                      (banner) => banner.placement === selectedPlacement,
                  )

        return [...list].sort((a, b) => {
            if (a.placement !== b.placement) {
                return a.placement.localeCompare(b.placement)
            }
            return a.order - b.order || a.id - b.id
        })
    }, [banners, selectedPlacement])

    const countByPlacement = useMemo(() => {
        return banners.reduce<Record<string, number>>((acc, banner) => {
            acc[banner.placement] = (acc[banner.placement] ?? 0) + 1
            return acc
        }, {})
    }, [banners])

    const handleToggle = async (banner: SiteBanner) => {
        try {
            const res = await toggleSiteBanner(banner.id)
            if (res.success) {
                setBanners((prev) =>
                    prev.map((item) =>
                        item.id === banner.id
                            ? { ...item, active: !item.active }
                            : item,
                    ),
                )
                toast.success(
                    banner.active ? 'Banner desativado' : 'Banner ativado',
                )
            } else {
                toast.error('Erro ao atualizar o banner')
            }
        } catch {
            toast.error('Erro ao atualizar o banner')
        }
    }

    /**
     * Move o banner dentro do próprio espaço. A ordem só faz sentido entre
     * banners do mesmo espaço, então a troca é sempre com o vizinho de placement.
     */
    const handleMove = async (banner: SiteBanner, direction: -1 | 1) => {
        const siblings = banners
            .filter((item) => item.placement === banner.placement)
            .sort((a, b) => a.order - b.order || a.id - b.id)

        const index = siblings.findIndex((item) => item.id === banner.id)
        const targetIndex = index + direction
        if (index < 0 || targetIndex < 0 || targetIndex >= siblings.length) return

        const reordered = [...siblings]
        const [moved] = reordered.splice(index, 1)
        reordered.splice(targetIndex, 0, moved)

        const items = reordered.map((item, position) => ({
            id: item.id,
            order: position,
        }))

        setReordering(true)
        try {
            const res = await reorderSiteBanners(items)
            if (res.success) {
                const orderById = new Map(items.map((item) => [item.id, item.order]))
                setBanners((prev) =>
                    prev.map((item) =>
                        orderById.has(item.id)
                            ? { ...item, order: orderById.get(item.id)! }
                            : item,
                    ),
                )
            } else {
                toast.error('Erro ao reordenar os banners')
            }
        } catch {
            toast.error('Erro ao reordenar os banners')
        } finally {
            setReordering(false)
        }
    }

    const handleDelete = async () => {
        if (!deletingId) return

        setDeleting(true)
        try {
            const res = await deleteSiteBanner(deletingId)
            if (res.success) {
                setBanners((prev) => prev.filter((item) => item.id !== deletingId))
                toast.success('Banner removido')
            } else {
                toast.error('Erro ao remover o banner')
            }
        } catch {
            toast.error('Erro ao remover o banner')
        } finally {
            setDeleting(false)
            setDeleteModalOpen(false)
            setDeletingId(null)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <MdViewCarousel className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">Banners do site</h1>
                        <p className="text-base-content/60 text-sm">
                            Controle as artes exibidas no site institucional
                            mediarsaude.com.br
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/marketing/site-banners/new')}
                    >
                        <MdAdd className="text-lg" />
                        Novo banner
                    </button>
                )}
            </div>

            <SiteVideoCard placements={placements} canUpdate={canUpdate} />

            {/* Filtro por espaço */}
            <div className="flex flex-wrap gap-2">
                <button
                    className={`btn btn-sm ${
                        selectedPlacement === ALL_PLACEMENTS
                            ? 'btn-secondary'
                            : 'btn-ghost'
                    }`}
                    onClick={() => setSelectedPlacement(ALL_PLACEMENTS)}
                >
                    Todos
                    <span className="badge badge-sm">{banners.length}</span>
                </button>
                {placements.map((placement) => (
                    <button
                        key={placement.key}
                        className={`btn btn-sm ${
                            selectedPlacement === placement.key
                                ? 'btn-secondary'
                                : 'btn-ghost'
                        }`}
                        onClick={() => setSelectedPlacement(placement.key)}
                        title={placement.description}
                    >
                        {placement.label}
                        <span className="badge badge-sm">
                            {countByPlacement[placement.key] ?? 0}
                        </span>
                    </button>
                ))}
            </div>

            {visibleBanners.length === 0 ? (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body items-center text-center">
                        <MdViewCarousel className="text-base-content/30 text-6xl" />
                        <h3 className="text-lg font-bold">
                            Nenhum banner cadastrado
                        </h3>
                        <p className="text-base-content/60">
                            Enquanto não houver banner ativo, o site exibe a arte
                            de reserva embutida no próprio site.
                        </p>
                        {canCreate && (
                            <button
                                className="btn btn-secondary btn-sm mt-2"
                                onClick={() =>
                                    navigate('/marketing/site-banners/new')
                                }
                            >
                                <MdAdd className="text-lg" />
                                Cadastrar o primeiro banner
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="card bg-base-100 shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th className="w-40">Arte</th>
                                    <th>Banner</th>
                                    <th>Espaço</th>
                                    <th>Exibição</th>
                                    <th>Situação</th>
                                    <th className="text-center">Ordem</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleBanners.map((banner) => {
                                    const status = bannerStatus(banner)
                                    return (
                                        <tr key={banner.id} className="hover">
                                            <td>
                                                <img
                                                    src={banner.imageUrl}
                                                    alt={banner.alt ?? banner.name}
                                                    className="bg-base-200 h-14 w-32 rounded object-cover"
                                                    loading="lazy"
                                                />
                                            </td>
                                            <td>
                                                <div className="font-medium">
                                                    {banner.name}
                                                </div>
                                                {banner.link && (
                                                    <a
                                                        href={banner.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="link link-hover text-base-content/50 flex max-w-xs items-center gap-1 truncate text-xs"
                                                    >
                                                        <MdOpenInNew />
                                                        {banner.link}
                                                    </a>
                                                )}
                                            </td>
                                            <td>
                                                <span className="badge badge-ghost badge-sm">
                                                    {placementLabel(banner.placement)}
                                                </span>
                                            </td>
                                            <td className="text-sm">
                                                {banner.startsAt || banner.endsAt ? (
                                                    <span>{formatWindow(banner)}</span>
                                                ) : (
                                                    <span className="text-base-content/40">
                                                        Sempre
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-secondary toggle-sm"
                                                        checked={banner.active}
                                                        disabled={!canUpdate}
                                                        onChange={() =>
                                                            handleToggle(banner)
                                                        }
                                                    />
                                                    <span
                                                        className={`badge badge-sm ${status.className}`}
                                                    >
                                                        {status.label}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        disabled={
                                                            reordering ||
                                                            !canUpdate
                                                        }
                                                        onClick={() =>
                                                            handleMove(banner, -1)
                                                        }
                                                        title="Subir"
                                                    >
                                                        <MdArrowUpward />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        disabled={
                                                            reordering ||
                                                            !canUpdate
                                                        }
                                                        onClick={() =>
                                                            handleMove(banner, 1)
                                                        }
                                                        title="Descer"
                                                    >
                                                        <MdArrowDownward />
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex justify-end gap-1">
                                                    {canUpdate && (
                                                        <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/marketing/site-banners/edit/${banner.id}`,
                                                                )
                                                            }
                                                            title="Editar"
                                                        >
                                                            <MdEdit className="text-lg" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            className="btn btn-ghost btn-sm text-error"
                                                            onClick={() => {
                                                                setDeletingId(
                                                                    banner.id,
                                                                )
                                                                setDeleteModalOpen(
                                                                    true,
                                                                )
                                                            }}
                                                            title="Remover"
                                                        >
                                                            <MdDelete className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false)
                    setDeletingId(null)
                }}
                onConfirm={handleDelete}
                title="Remover banner"
                message="O banner sai do ar imediatamente após a remoção. Deseja continuar?"
                loading={deleting}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
